import React, { useState } from 'react';
import {
  Visibility,
  VisibilityOff,
  ArrowForward as ArrowIcon,
  ErrorOutline as ErrorIcon
} from '@mui/icons-material';
import { loginUser } from '../utils/authUtils';
import BrandMark from './common/BrandMark';
import '../styles/LoginPage.css';

// What the product covers — shown on the brand panel as a quiet index, not marketing copy.
const MODULES = [
  { code: '01', label: 'Gate passes', detail: 'Entry and exit, weighed at the gate' },
  { code: '02', label: 'Batches', detail: 'Sourcing, grading, processing, dispatch' },
  { code: '03', label: 'Invoices & purchases', detail: 'Local and export, with payments' },
  { code: '04', label: 'Ledger', detail: 'Double-entry, posted automatically' },
];

const LoginPage = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await loginUser(credentials);

      if (result.success) {
        onLogin(result.token);
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Unable to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="login-page">
      {/* Brand panel */}
      <aside className="login-aside" aria-hidden="true">
        <div className="login-aside-top">
          <div className="login-brand">
            <div className="login-brand-mark"><BrandMark /></div>
            <span>InventoryOS</span>
          </div>
        </div>

        <div className="login-aside-body">
          <p className="login-eyebrow">Inventory &amp; trade management</p>
          <h2 className="login-headline">
            From the gate to the ledger, <em>one record.</em>
          </h2>
          <ol className="login-modules">
            {MODULES.map(m => (
              <li key={m.code}>
                <span className="login-module-code">{m.code}</span>
                <span className="login-module-text">
                  <span className="login-module-label">{m.label}</span>
                  <span className="login-module-detail">{m.detail}</span>
                </span>
              </li>
            ))}
          </ol>
        </div>

        <div className="login-aside-foot">
          Receive · Grade · Sell · Settle
        </div>
      </aside>

      {/* Form panel */}
      <main className="login-main">
        <div className="login-card">
          <div className="login-mobile-brand">
            <div className="login-brand-mark"><BrandMark /></div>
            <span>InventoryOS</span>
          </div>

          <header className="login-header">
            <h1 className="login-title">Sign in</h1>
            <p className="login-subtitle">Use the username and password your administrator gave you.</p>
          </header>

          <form onSubmit={handleSubmit} className="login-form">
            <div className="login-form-group">
              <label htmlFor="login-username" className="login-label">Username</label>
              <input
                id="login-username"
                type="text"
                name="username"
                className={`login-input ${error ? 'login-input-error' : ''}`}
                placeholder="e.g. ahmed.raza"
                value={credentials.username}
                onChange={handleInputChange}
                required
                autoFocus
                autoComplete="username"
                aria-invalid={!!error}
                aria-describedby={error ? 'login-error' : undefined}
              />
            </div>

            <div className="login-form-group">
              <label htmlFor="login-password" className="login-label">Password</label>
              <div className="login-password-container">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  className={`login-input ${error ? 'login-input-error' : ''}`}
                  placeholder="Your password"
                  value={credentials.password}
                  onChange={handleInputChange}
                  required
                  autoComplete="current-password"
                  aria-invalid={!!error}
                  aria-describedby={error ? 'login-error' : undefined}
                />
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={togglePasswordVisibility}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </button>
              </div>
            </div>

            {error && (
              <div className="login-error" id="login-error" role="alert">
                <ErrorIcon className="login-error-icon" aria-hidden="true" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              className="login-submit-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="login-loading-spinner" aria-hidden="true"></span>
                  Signing in…
                </>
              ) : (
                <>
                  Sign in
                  <ArrowIcon className="login-submit-icon" aria-hidden="true" />
                </>
              )}
            </button>
          </form>

          <p className="login-foot">On a shared computer, remember to sign out when you're done.</p>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
